/**
 * ADR 0457 D2 — what a job's cargo looks like, counted ONE way for the whole job page:
 * the stat card (D3) · the overview timeline (D4) · the cargo tab (D5). Components never count on their own.
 * plan: 4-Archives/plan/tms/0457-job-name-and-cargo-tracking.md
 *
 * Rules (pinned by `cargo-summary.cases.json` → `apps/web/src/lib/cargo/cargo-summary.test.ts`):
 * - `cancelled` legs are not counted anywhere.
 * - a return item counts ONCE in the "return" box, by its collecting (pickup) leg (ADR 0407) — its drop-off
 *   leg still counts at its own stop (`byStop`).
 * - a deliver leg whose pickup got nothing (failed / picked 0) and is still pending is not counted
 *   (ADR 0456 R3 "ไม่มีของต้องส่ง" — same rule as the driver app's `cargoCounts`).
 * - `attention` = failed | partial legs, ordered by stop seq then item code.
 *
 * Where it is used (update this list when adding one): `apps/web/src/hooks/use-ticket-cargo.ts` →
 * `jobs/tickets/_shared.tsx` (stat card) · `components/cargo/StopCargoItems.tsx` (overview) ·
 * `jobs/tickets/tabs/CargoTab.tsx` + `components/cargo/CargoStopGroups.tsx` (cargo tab).
 * Not used for: the jobs list (Q2 — that one is `cargoCounts`, same as the driver app) · `GET cargo/items/summary`.
 */
import { pickedQty } from './cargo-item-rules';

export const CARGO_SUMMARY_BOXES = ['pickup', 'deliver', 'return'] as const;
export type CargoSummaryBox = (typeof CARGO_SUMMARY_BOXES)[number];

/** the four legs people talk about: รับขึ้น · ส่งของ · เก็บคืน · ส่งคืน */
export const CARGO_LEG_KINDS = ['pickup', 'deliver', 'returnPickup', 'returnDeliver'] as const;
export type CargoLegKind = (typeof CARGO_LEG_KINDS)[number];

export interface CargoSummaryRow {
  _id: string;
  display_id?: string;
  stop_id: string;
  /** 'pickup' | 'deliver' · old single rows may still say 'return' (read as a return pickup) */
  action: string;
  pair_kind?: 'normal' | 'return' | null;
  other_leg_id?: string | null;
  status: 'pending' | 'done' | 'partial' | 'failed' | 'cancelled';
  qty: number;
  qty_done?: number | null;
}

export interface CargoLegCount { total: number; done: number; problem: number }

export interface TicketCargoSummary<R extends CargoSummaryRow = CargoSummaryRow> {
  /** products (a pickup + deliver pair = 1), not legs */
  items: number;
  byAction: Record<CargoSummaryBox, CargoLegCount>;
  /** every counted leg AT a stop (a return's drop-off leg counts at its own stop) */
  byStop: Record<string, CargoLegCount>;
  /** the same legs as `byStop`, split by leg kind — the cargo tab's stop header ("รับขึ้น 2/8 · เก็บคืน 0/1") */
  byStopLeg: Record<string, Partial<Record<CargoLegKind, CargoLegCount>>>;
  attention: R[];
}

export function cargoLegKind(r: Pick<CargoSummaryRow, 'action' | 'pair_kind'>): CargoLegKind {
  if (r.pair_kind === 'return') return r.action === 'deliver' ? 'returnDeliver' : 'returnPickup';
  if (r.action === 'return') return 'returnPickup';
  return r.action === 'deliver' ? 'deliver' : 'pickup';
}

const isProblem = (s: string) => s === 'failed' || s === 'partial';
const empty = (): CargoLegCount => ({ total: 0, done: 0, problem: 0 });

/** ADR 0456 R3 — a pending deliver leg whose pickup got nothing: no goods to deliver, not counted (shown greyed) */
export function cargoNoGoods<R extends CargoSummaryRow>(r: R, byId: ReadonlyMap<string, R>): boolean {
  if (r.action !== 'deliver' || r.status !== 'pending' || !r.other_leg_id) return false;
  return pickedQty(byId.get(r.other_leg_id) ?? null) === 0;
}

export function ticketCargoSummary<R extends CargoSummaryRow>(rows: readonly R[], stopSeq: Record<string, number>): TicketCargoSummary<R> {
  const byId = new Map(rows.map((r) => [r._id, r] as const));
  const byAction: Record<CargoSummaryBox, CargoLegCount> = { pickup: empty(), deliver: empty(), return: empty() };
  const byStop: Record<string, CargoLegCount> = {};
  const byStopLeg: Record<string, Partial<Record<CargoLegKind, CargoLegCount>>> = {};
  const attention: R[] = [];
  const products = new Set<string>();
  for (const r of rows) {
    if (r.status === 'cancelled' || cargoNoGoods(r, byId)) continue;
    const kind = cargoLegKind(r);
    for (const st of [(byStop[r.stop_id] ??= empty()), ((byStopLeg[r.stop_id] ??= {})[kind] ??= empty())]) {
      st.total++;
      if (r.status === 'done') st.done++;
      if (isProblem(r.status)) st.problem++;
    }
    if (isProblem(r.status)) attention.push(r);
    if (kind === 'returnDeliver') continue; // counted once, by the collecting leg
    const box: CargoSummaryBox = kind === 'returnPickup' ? 'return' : (kind as 'pickup' | 'deliver');
    byAction[box].total++;
    if (r.status === 'done') byAction[box].done++;
    if (isProblem(r.status)) byAction[box].problem++;
  }
  for (const r of rows) if (r.status !== 'cancelled') products.add(pairKey(r, byId));
  const seq = (id: string) => stopSeq[id] ?? Number.MAX_SAFE_INTEGER;
  attention.sort((a, b) => seq(a.stop_id) - seq(b.stop_id) || (a.display_id ?? '').localeCompare(b.display_id ?? ''));
  return { items: products.size, byAction, byStop, byStopLeg, attention };
}

/** one product = one pair: both legs share a key (the pickup leg's id when it is known) */
function pairKey<R extends CargoSummaryRow>(r: R, byId: Map<string, R>): string {
  if (r.action === 'pickup' || !r.other_leg_id || !byId.has(r.other_leg_id)) return r._id;
  return r.other_leg_id;
}

/** the legs at one stop in the order the driver works them: รับขึ้น → ส่งของ → เก็บคืน → ส่งคืน, then item code */
export function legsAtStop<R extends CargoSummaryRow>(rows: readonly R[], stopId: string, opts: { includeCancelled?: boolean } = {}): R[] {
  const rank = (r: R) => CARGO_LEG_KINDS.indexOf(cargoLegKind(r));
  // the cargo tab's "ยกเลิก" filter lists cancelled legs in their stop group (ADR 0457 D5); everywhere else they are hidden
  return rows.filter((r) => r.stop_id === stopId && (opts.includeCancelled || r.status !== 'cancelled'))
    .sort((a, b) => rank(a) - rank(b) || (a.display_id ?? '').localeCompare(b.display_id ?? ''));
}
